import React, { useLayoutEffect, useRef, useEffect } from "react";
import { Textarea, FormLabel } from "@chakra-ui/react";

const AutoResizeTextarea = ({
   value,
   label = null,
   isCode = false,
   size = "sm",
   onChange,
   minHeight = "80px",
   ...props 
  }) => {
  const ref = useRef(null);
  const rafIdRef = useRef(null);

  const measureAndSet = () => {
    const el = ref.current;
    if (!el) return;

    el.style.height = "auto";
    let targetHeight = el.scrollHeight;

    // Hidden (e.g., inactive TabPanel) fallback
    if (!targetHeight) {
      const prev = {
        position: el.style.position,
        visibility: el.style.visibility,
        display: el.style.display,
        height: el.style.height,
      };
      el.style.position = "absolute";
      el.style.visibility = "hidden";
      el.style.display = "block";
      el.style.height = "auto";
      targetHeight = el.scrollHeight || 0;
      Object.assign(el.style, prev);
    }

    const minPx = parseInt(minHeight, 10) || 0;
    el.style.height = Math.max(targetHeight, minPx) + "px";
  };

  const scheduleResize = () => {
    if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
    rafIdRef.current = requestAnimationFrame(measureAndSet);
  };

  useLayoutEffect(() => {
    scheduleResize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) scheduleResize();
    });
    io.observe(el);

    const container = el.parentElement || el;
    const ro = new ResizeObserver(() => scheduleResize());
    ro.observe(container);

    const onWinResize = () => scheduleResize();
    window.addEventListener("resize", onWinResize);

    return () => {
      io.disconnect();
      ro.disconnect();
      window.removeEventListener("resize", onWinResize);
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
    };
  }, []);

return (
  <>
    {label && (
      <FormLabel htmlFor={props.id} fontSize={size}>
        {label}
      </FormLabel>
    )}

    <Textarea
      fontSize={size}
      ref={ref}
      value={value ?? ""}
      onChange={onChange}
      resize="none"          // remove manual handle
      overflow="hidden"
      minHeight={minHeight}
      w="100%"
      // If isCode = true then Monospace goodness for YAML
      spellCheck={isCode ? false : undefined}
      wrap={isCode ? "off" : undefined}
      style={{
        boxSizing: "border-box",
        ...(isCode && {
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono', Monaco, 'Courier New', monospace",
          fontSize: size,
          lineHeight: "1.4",
          whiteSpace: "pre",
          spellCheck: false
        }),
      }}
      {...props}
    />
  </>
);
};

export default AutoResizeTextarea;
